// Types for Plybook module (Hojas de Laminado)

export type PlybookStatus = 'draft' | 'approved' | 'obsolete';

export type MaterialType =
  | 'carbon_fiber'
  | 'fiberglass'
  | 'kevlar'
  | 'hybrid'
  | 'prepreg'
  | 'dry_fiber'
  | 'core_material'
  | 'adhesive'
  | 'other';

export type FiberOrientation = 'warp' | 'weft' | 'bias' | 'random';

// Zonas dinámicas del molde
export interface PlybookZone {
  id: string;
  name: string;
  color: string;
  sort_order: number;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Posición de zona por plybook
export interface PlybookZonePosition {
  id: string;
  plybook_id: string;
  zone_id: string;
  position_x: number | null;
  position_y: number | null;
  zone?: PlybookZone;
  created_at: string;
  updated_at: string;
}

// Legacy type for backwards compatibility
export type PlyZone = string;

export interface Plybook {
  id: string;
  name: string;
  code?: string;
  description?: string;
  mold_id?: string;
  customer_id?: string;
  project_id?: string;
  version: string;
  status: PlybookStatus;
  total_plies: number;
  total_weight_grams?: number;
  total_thickness_mm?: number;
  approved_by?: string;
  approved_at?: string;
  notes?: string;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // File attachments
  main_image_url?: string;
  cad_file_url?: string;
  pdf_file_url?: string;
  attachments?: PlybookAttachment[];
  // Relations
  customer?: {
    id: string;
    name: string;
  };
  project?: {
    id: string;
    name: string;
    reference_code?: string;
  };
  mold?: {
    id: string;
    name: string;
    code?: string;
  };
  approved_by_profile?: {
    id: string;
    full_name?: string;
  };
  created_by_profile?: {
    id: string;
    full_name?: string;
  };
  plies?: PlybookPly[];
}

export interface PlybookPly {
  id: string;
  plybook_id: string;
  ply_number: number;
  material_id?: string;
  material_name?: string;
  material_type?: MaterialType;
  fiber_angle: number;
  orientation?: FiberOrientation;
  width_mm?: number;
  length_mm?: number;
  area_m2?: number;
  weight_grams?: number;
  position_x: number;
  position_y: number;
  zone?: PlyZone;
  is_full_coverage: boolean;
  special_instructions?: string;
  image_url?: string;
  color: string;
  created_at: string;
  updated_at: string;
  // Relations
  material?: {
    id: string;
    name: string;
    category?: string;
  };
}

export interface PlybookHistory {
  id: string;
  plybook_id: string;
  version: string;
  snapshot: Record<string, any>;
  changes_summary?: string;
  changed_by?: string;
  changed_at: string;
  changed_by_profile?: {
    id: string;
    full_name?: string;
  };
}


export type AttachmentType = 'pdf' | 'cad' | 'image' | 'document' | 'other';

export interface PlybookAttachment {
  id: string;
  plybook_id: string;
  file_name: string;
  file_url: string;
  file_type?: string;
  file_size?: number;
  attachment_type: AttachmentType;
  description?: string;
  version?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
  uploaded_by_profile?: {
    id: string;
    full_name?: string;
  };
}

export const ATTACHMENT_TYPE_LABELS: Record<AttachmentType, string> = {
  pdf: 'PDF',
  cad: 'CAD',
  image: 'Imagen',
  document: 'Documento',
  other: 'Otro',
};

export const ATTACHMENT_TYPE_ICONS: Record<AttachmentType, string> = {
  pdf: 'FileText',
  cad: 'Box',
  image: 'Image',
  document: 'File',
  other: 'Paperclip',
};

// Form data types
export interface PlybookFormData {
  name: string;
  code?: string;
  description?: string;
  customer_id?: string;
  project_id?: string;
  mold_id?: string;
  version?: string;
  notes?: string;
}


export interface PlyFormData {
  ply_number: number;
  material_id?: string;
  material_name?: string;
  material_type?: MaterialType;
  fiber_angle: number;
  orientation?: FiberOrientation;
  width_mm?: number;
  length_mm?: number;
  area_m2?: number;
  weight_grams?: number;
  position_x?: number;
  position_y?: number;
  zone?: PlyZone;
  is_full_coverage?: boolean;
  special_instructions?: string;
  color?: string;
}


// Constants
export const FIBER_ANGLES = [0, 45, 90, -45, 30, 60, -30, -60, 9090, 4545] as const;

export function formatFiberAngle(angle: number | null | undefined): string {
  if (angle === null || angle === undefined) return '-';
  if (angle === 9090) return '0/90';
  if (angle === 4545) return '±45';
  return `${angle}°`;
}


export const PLY_COLORS: Record<MaterialType, string> = {
  carbon_fiber: '#1e293b',
  fiberglass: '#fef3c7',
  kevlar: '#fde047',
  hybrid: '#a78bfa',
  prepreg: '#0ea5e9',
  dry_fiber: '#e2e8f0',
  core_material: '#fed7aa',
  adhesive: '#bbf7d0',
  other: '#94a3b8',
};

export const MATERIAL_TYPE_LABELS: Record<MaterialType, string> = {
  carbon_fiber: 'Fibra de Carbono',
  fiberglass: 'Fibra de Vidrio',
  kevlar: 'Kevlar',
  hybrid: 'Híbrido',
  prepreg: 'Prepreg',
  dry_fiber: 'Fibra Seca',
  core_material: 'Material de Núcleo',
  adhesive: 'Adhesivo',
  other: 'Otro',
};

// PLY_ZONE_LABELS eliminado - ahora las zonas son dinámicas desde la BD

export const PLYBOOK_STATUS_LABELS: Record<PlybookStatus, string> = {
  draft: 'Borrador',
  approved: 'Aprobado',
  obsolete: 'Obsoleto',
};

export const PLYBOOK_STATUS_COLORS: Record<PlybookStatus, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  obsolete: 'bg-gray-100 text-gray-800',
};

// ============================================
// EXECUTION TYPES (Integración con WorkOrders)
// ============================================

export type ExecutionStatus = 'pending' | 'in_progress' | 'completed' | 'paused';

export interface PlybookExecution {
  id: string;
  work_order_id: string;
  plybook_id: string;
  process_record_id?: string;
  started_at?: string;
  completed_at?: string;
  status: ExecutionStatus;
  total_plies: number;
  confirmed_plies: number;
  operator_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Relations
  work_order?: {
    id: string;
    order_number: string;
    product_name?: string;
  };
  plybook?: Plybook;
  operator?: {
    id: string;
    full_name?: string;
  };
  confirmations?: PlyConfirmation[];
}

export interface PlyConfirmation {
  id: string;
  execution_id: string;
  ply_id: string;
  ply_number: number;
  plotter_cut_id?: string;
  material_lot_id?: string;
  confirmed_at: string;
  confirmed_by?: string;
  duration_seconds?: number;
  weight_actual_grams?: number;
  notes?: string;
  has_defect: boolean;
  defect_description?: string;
  created_at: string;
  // Relations
  ply?: PlybookPly;
  plotter_cut?: {
    id: string;
    package_code: string;
    batch_number?: string;
  };
  confirmed_by_profile?: {
    id: string;
    full_name?: string;
  };
}


export const EXECUTION_STATUS_LABELS: Record<ExecutionStatus, string> = {
  pending: 'Pendiente',
  in_progress: 'En Progreso',
  completed: 'Completado',
  paused: 'Pausado',
};

export const EXECUTION_STATUS_COLORS: Record<ExecutionStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
};
